import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

const STEP_CLS = {
  0: "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]",
  1: "border-[#B7791F]/40 bg-[#B7791F]/10 text-[#8a5d14]",
  2: "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]",
  3: "border-[#C0392B] bg-[#C0392B] text-white",
};

const wkCls = (p) =>
  p == null ? "bg-stone-100 text-stone-400" : p >= 90 ? "bg-[#27AE60]/15 text-[#1e8449]" : "bg-[#C0392B]/15 text-[#C0392B]";

export function EvvLadder() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/evv/ladder").then((r) => setData(r.data))
    .catch((e) => { toast.error(formatApiError(e)); setData({ caregivers: [], weeks: [], threshold: 90 }); });
  useEffect(() => { load(); }, []);

  const act = async (c, kind) => {
    const prompts = {
      strict_on: `Place ${c.name} on strict schedule (last step before termination)? Optional note for the record:`,
      strict_off: `Lift strict schedule for ${c.name} and return them to on-track? Optional note:`,
      resolve: `Return ${c.name} to on-track? Optional note (e.g. what fixed it):`,
    };
    const note = window.prompt(prompts[kind], "");
    if (note === null) return;
    setBusy(c.uid + kind);
    try {
      if (kind === "resolve") await api.post(`/evv/ladder/${c.uid}/resolve`, { note });
      else await api.post(`/evv/ladder/${c.uid}/strict-schedule`, { on: kind === "strict_on", note });
      toast.success(`Updated ${c.name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  const runNow = async () => {
    if (!window.confirm("Run the weekly ladder now for last week? This applies step changes and sends the step emails — same as the Monday automatic run.")) return;
    setBusy("run");
    try {
      await api.post("/evv/ladder/run");
      toast.success("Ladder run complete — numbers and steps are current");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };

  if (data === null) return <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-4" data-testid="evv-ladder">
      <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-stone-900">How the ladder works</p>
            <p className="mt-0.5 max-w-3xl text-xs text-stone-500">
              Every Monday, last week's per-employee EVV report (uploaded above, Mon–Sun) advances the ladder.
              Two weeks in a row under {data.threshold}% assigns a mandatory EVV policy review to sign. Still under after signing
              escalates to an office meeting. The office can then set a strict schedule with pre-shift reminders and same-day
              no-clock-in alerts — the last step before termination. A week back at {data.threshold}%+ moves steps 1–2 back to
              on-track automatically. The monthly insurer report remains the official company-wide number.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={runNow} disabled={busy === "run"} className="shrink-0 border-stone-300 text-xs" data-testid="evv-ladder-run">
            {busy === "run" ? "Running…" : "Run the ladder now"}
          </Button>
        </div>
        {!data.policy_published && (
          <p className="mt-2 rounded-md border border-[#B7791F]/40 bg-[#B7791F]/5 px-3 py-2 text-xs text-[#8a5d14]" data-testid="evv-ladder-policy-warning">
            The EVV policy is still a draft — publish it on the Policies page so mandatory reviews can actually be signed.
          </p>
        )}
      </div>
      {data.caregivers.length === 0 ? (
        <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-400" data-testid="evv-ladder-empty">No weekly EVV reports in the last 4 weeks yet — upload the weekly per-employee EVV report on the Analysis tab.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-stone-200 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-stone-200 bg-secondary/60 text-xs uppercase tracking-wide text-stone-500">
                <th className="px-4 py-3 font-medium">Caregiver</th>
                {data.weeks.map((w) => <th key={w} className="px-3 py-3 text-center font-medium">wk {w.slice(5)}</th>)}
                <th className="px-3 py-3 font-medium">Step</th>
                <th className="px-3 py-3 font-medium">Policy review</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.caregivers.map((c) => (
                <tr key={c.uid} className="border-b border-stone-100 last:border-0" data-testid={`evv-ladder-row-${c.uid}`}>
                  <td className="px-4 py-2.5">
                    <Link to={`/team/${c.uid}`} className="font-medium text-stone-800 hover:text-primary hover:underline">{c.name}</Link>
                    {c.consecutive_below > 0 && <p className="text-[10px] text-[#C0392B]">{c.consecutive_below} wk(s) under {data.threshold}%</p>}
                  </td>
                  {c.weeks.map((w) => (
                    <td key={w.week} className="px-3 py-2.5 text-center">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${wkCls(w.pct)}`} title={w.total ? `${w.verified}/${w.total} visits verified` : "no visits"}>
                        {w.pct != null ? `${w.pct}%` : "—"}
                      </span>
                    </td>
                  ))}
                  <td className="px-3 py-2.5">
                    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STEP_CLS[c.step] || STEP_CLS[0]}`} data-testid={`evv-ladder-step-${c.uid}`}>
                      {c.step_label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-stone-500">
                    {c.policy_review_signed === null ? "—" : c.policy_review_signed ? "Signed ✓" : c.step1_assigned_at ? "Assigned — unsigned" : "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1.5">
                      {c.step > 0 && (
                        <Button size="sm" variant="outline" onClick={() => act(c, "resolve")} disabled={!!busy}
                          className="border-stone-300 text-xs" data-testid={`evv-ladder-resolve-${c.uid}`}>On track</Button>
                      )}
                      {c.strict_schedule ? (
                        <Button size="sm" variant="outline" onClick={() => act(c, "strict_off")} disabled={!!busy}
                          className="border-[#C0392B]/40 text-xs text-[#C0392B]" data-testid={`evv-ladder-lift-${c.uid}`}>Lift strict schedule</Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => act(c, "strict_on")} disabled={!!busy}
                          className="border-[#C0392B]/40 text-xs text-[#C0392B]" data-testid={`evv-ladder-strict-${c.uid}`}>Strict schedule</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
